/**
 * Token-Aware Context Router — selects the most relevant scaffold sections
 * for a given task within a token budget.
 *
 * Fully deterministic: no AI calls, no network.
 * Ranking signals: term match, preference boost, telemetry boost, edge bonus.
 */
import type { Section } from "./section-index.js";
import type { FileAggregation } from "../telemetry/query-log.js";
import { extractKeywords } from "./section-index.js";

export interface RouterInput {
  task: string;
  budget: number;
  prefer?: string[];
}

export interface ScoredSection {
  id: string;
  content: string;
  tokens: number;
  score: number;
}

export interface RouterOutput {
  sections: ScoredSection[];
  total_tokens: number;
  budget_remaining: number;
  omitted: Array<{ id: string; tokens: number; score: number }>;
}

const DEFAULT_BUDGET = 4000;

/**
 * Compute a hot-path multiplier for a file based on query frequency.
 * Log-scaled, range: 1.0 (never queried) to 1.5 (very hot).
 * Adapted from src/drift/severity.ts — capped lower for routing context.
 */
function telemetryBoost(file: string, byFile: Map<string, FileAggregation>): number {
  const agg = byFile.get(file);
  if (!agg) return 1.0;
  const bonus = Math.min(0.5, Math.log2(agg.hits + 1) / 8);
  return 1.0 + bonus;
}

/**
 * Score a section against the task.
 *
 * Signals:
 * 1. Term match (0-1): fraction of task terms found in section keywords/content
 * 2. Preference boost (+0.3): if section file matches a prefer hint
 * 3. Telemetry boost (1.0-1.5): multiplier from query frequency
 * 4. Edge bonus (+0.2): applied externally after initial scoring
 */
function scoreSection(
  section: Section,
  taskTerms: string[],
  prefer: string[],
  byFile: Map<string, FileAggregation>
): number {
  if (taskTerms.length === 0) return 0;

  // 1. Term match — check against keywords and content
  const contentLower = section.content.toLowerCase();
  let matched = 0;
  for (const term of taskTerms) {
    if (section.keywords.includes(term) || contentLower.includes(term)) {
      matched++;
    }
  }
  let score = matched / taskTerms.length;

  // 2. Preference boost
  if (prefer.length > 0) {
    const fileLower = section.file.toLowerCase();
    for (const hint of prefer) {
      if (fileLower.includes(hint.toLowerCase())) {
        score += 0.3;
        break;
      }
    }
  }

  // 3. Telemetry boost (multiplicative)
  score *= telemetryBoost(section.file, byFile);

  return score;
}

/**
 * Route context: rank all sections, apply edge bonuses, pack by budget.
 */
export function routeContext(
  input: RouterInput,
  index: Section[],
  telemetry: FileAggregation[] = [],
  edgeMap: Map<string, string[]> = new Map()
): RouterOutput {
  const budget = input.budget || DEFAULT_BUDGET;
  const prefer = input.prefer ?? [];
  const taskTerms = extractKeywords(input.task);

  // Build telemetry lookup
  const byFile = new Map<string, FileAggregation>();
  for (const a of telemetry) byFile.set(a.file, a);

  // Score all sections
  const scored: Array<{ section: Section; score: number }> = index.map((section) => ({
    section,
    score: scoreSection(section, taskTerms, prefer, byFile),
  }));

  // Edge bonus: if a high-scoring section's file has edges to another file,
  // boost sections from that target file
  const selectedFiles = new Set<string>();
  // First pass: find files with score > 0.3 to determine edge sources
  for (const { section, score } of scored) {
    if (score > 0.3) selectedFiles.add(section.file);
  }
  for (const { section, score } of scored) {
    if (score > 0) continue; // only boost sections that would otherwise rank low
    // Check if any selected file has an edge to this section's file
    for (const sourceFile of selectedFiles) {
      const targets = edgeMap.get(sourceFile);
      if (targets?.includes(section.file)) {
        // Apply edge bonus — mutate score in place
        const entry = scored.find((s) => s.section === section);
        if (entry) entry.score += 0.2;
        break;
      }
    }
  }

  // Sort by score descending, then by stability (decisions/stack first for prompt cache efficiency),
  // then by tokens ascending (prefer smaller sections on tie)
  const STABILITY_ORDER: Record<string, number> = {
    decisions: 0, stack: 1, architecture: 2, conventions: 3, setup: 4,
  };
  function stabilityOf(file: string): number {
    const lower = file.toLowerCase();
    for (const [key, order] of Object.entries(STABILITY_ORDER)) {
      if (lower.includes(key)) return order;
    }
    return 5; // patterns and unknown files come last
  }
  scored.sort((a, b) =>
    b.score - a.score ||
    stabilityOf(a.section.file) - stabilityOf(b.section.file) ||
    a.section.tokens - b.section.tokens
  );

  // Greedy budget packing
  const selected: ScoredSection[] = [];
  const omitted: Array<{ id: string; tokens: number; score: number }> = [];
  let totalTokens = 0;

  for (const { section, score } of scored) {
    if (score <= 0) {
      // Don't include zero-relevance sections
      continue;
    }
    if (totalTokens + section.tokens <= budget) {
      selected.push({
        id: section.id,
        content: section.content,
        tokens: section.tokens,
        score: Math.round(score * 1000) / 1000,
      });
      totalTokens += section.tokens;
    } else {
      omitted.push({
        id: section.id,
        tokens: section.tokens,
        score: Math.round(score * 1000) / 1000,
      });
    }
  }

  return {
    sections: selected,
    total_tokens: totalTokens,
    budget_remaining: budget - totalTokens,
    omitted,
  };
}
